<template>
  <Layout>
    <div>
      <h1>Abstract Overview</h1>
      
      <div class="row mb-3">
        <div class="col-md-6">
          <button class="btn btn-primary" @click="showSubmitModal" style="background-color: darkseagreen; border-color: darkseagreen;">
            <i class="fa fa-plus"></i> Submit New Abstract
          </button>
        </div>
        <div class="col-md-6">
          <div class="input-group">
            <select 
              class="form-control" 
              v-model="selectedFilter"
              @change="filterAbstracts"
            >
              <option value="all">All Sessions</option>
              <option v-for="session in allSessionsList" :key="session.id" :value="session.id">{{ session.session_no }} - {{ session.title }}</option>
            </select>
            <div class="input-group-append">
              <button class="btn btn-outline-secondary" type="button" @click="clearFilter">
                <i class="fa fa-times"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
      
      <div class="abstract-list">
        <div class="card abstract-item" v-for="abstract in filteredAbstracts" :key="abstract.id">
          <div class="card-body">
            <div class="row">
              <div class="col-md-8">
                <h5 class="card-title">{{ abstract.title }}</h5>
                <div class="abstract-meta">
                  <span class="badge badge-info">{{ abstract.mode }}</span>     
                  <!-- <span class="badge badge-info">{{ abstract.category }}</span> -->
                  <span class="badge badge-warning">Submitted</span>
                </div>
                <div> 
                  <strong>Payment status:</strong> {{ abstract.payment_status }}
                </div>
                <div class="abstract-authors mt-2">
                  <strong>Authors:</strong>
                  <div class="authors-list">
                    <div 
                      v-for="(author, index) in getAuthorsList(abstract.authors_new)" 
                      :key="index"
                      class="author-item"
                    >
                      {{ author.trim() }}
                    </div>
                  </div>
                </div>
              </div>
              <div class="col-md-4 text-right">
                <div class="abstract-info">
                  <p><strong>Abstract Code:</strong> {{ abstract.ab_no }}</p>
                  <p><strong>Submission time:</strong> {{ formatDate(abstract.createtime) }}</p>
                  <p><strong>Session:</strong> {{ formatSession(abstract.session) }}</p>
                  <p><strong>Review Status:</strong> {{ getStatusText(abstract.status) }}</p>
                </div>
                <div class="abstract-actions mt-3">
                  <button class="btn btn-outline-primary btn-sm" @click="viewAbstract(abstract)">
                    View Details
                  </button>
                  <button class="btn btn-outline-secondary btn-sm ml-2" @click="editAbstract(abstract)" v-if="abstract.status === 0 || abstract.payment_status  == 2 ">
                    Edit
                  </button>
                  <button class="btn btn-outline-danger btn-sm ml-2" @click="deleteAbstract(abstract)" v-if="abstract.status == 0 || abstract.payment_status == 'pending' ">

                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Submit Abstract Modal -->
      <div class="modal" :class="{ 'show d-block': showSubmitForm || showEditForm }" tabindex="-1" v-if="showSubmitForm || showEditForm">
        <div class="modal-dialog modal-lg">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title">{{ isEditing ? 'Edit Abstract' : 'Submit New Abstract' }}</h5>
              <button type="button" class="close" @click="closeModal">
                <span>&times;</span>
              </button>
            </div>
            <div class="modal-body">
              <form @submit.prevent="handleSubmit" class="scrollable-form">
                <div class="form-group">
                  <label for="abstractTitle">Title *</label>
                  <input 
                    type="text" 
                    class="form-control" 
                    id="abstractTitle" 
                    v-model="newAbstract.title"
                    required
                  >
                </div>
                
                <div class="form-group">
                  <label for="abstractType">Presentation Type *</label>
                  <select class="form-control" id="abstractType" v-model="newAbstract.mode" required>
                    <option value="">Select Type</option>
                    <option value="oral">Oral Presentation</option>
                    <option value="poster">Poster Presentation</option>
                    <option value="either">Either</option>
                  </select>
                </div>
                
                <div class="form-group">
                  <label for="abstractSession">Session *</label>
                  <select class="form-control" id="abstractSession" v-model="newAbstract.session_id" @change="onSessionChange" required>
                    <option value="">{{ loadingSessions ? 'Loading sessions...' : 'Select Session' }}</option>
                    <option v-for="session in sessions" :key="session.id" :value="session.id">{{ session.session_no }} - {{ session.sessionname || session.title }}</option>
                  </select>
                  <small v-if="loadingSessions" class="form-text text-muted loading-sessions">
                    Loading available sessions...
                  </small>
                </div>
                
                <div class="form-group">
                  <label for="registrationType">Registration Type *</label>
                  <select class="form-control" id="registrationType" v-model="newAbstract.registration_type" :disabled="true" required>
                    <option value="">{{ loadingTickets ? 'Loading tickets...' : 'Select Registration Type' }}</option>
                    <option v-for="ticket in filteredTickets" :key="ticket.id" :value="ticket.id">
                      {{ ticket.type }} - {{ ticket.currency }} {{ ticket.fee }}
                    </option>
                  </select>
                  <small v-if="loadingTickets" class="form-text text-muted">
                    Loading available registration types...
                  </small>
                </div>
                
                <div class="form-group">
                  <label>Authors *</label>
                  <div v-for="(author, idx) in newAbstract.authors" :key="idx" class="author-block mb-3 p-3 border rounded">
                    <div class="form-row">
                      <div class="col-md-6 mb-2">
                        <label class="small text-muted">Email *</label>
                        <input 
                          type="email" 
                          class="form-control" 
                          v-model="author.email" 
                          placeholder="Email" 
                          @blur="fetchAuthorInfo(idx)" 
                          required
                        >
                      </div>
                      <div class="col-md-6 mb-2">
                        <label class="small text-muted">Title</label>
                        <select class="form-control" v-model="author.title">
                          <option value="">Select Title</option>
                          <option value="Mr">Mr</option>
                          <option value="Ms">Ms</option>
                          <option value="Dr">Dr</option>
                          <option value="Prof">Prof</option>
                        </select>
                      </div>
                    </div>
                    <div class="form-row">
                      <div class="col-md-6 mb-2">
                        <label class="small text-muted">First Name *</label>
                        <input 
                          type="text" 
                          class="form-control" 
                          v-model="author.first_name" 
                          placeholder="First Name" 
                          :readonly="author.found" 
                          required
                        >
                      </div>
                      <div class="col-md-6 mb-2">
                        <label class="small text-muted">Last Name *</label>
                        <input 
                          type="text" 
                          class="form-control" 
                          v-model="author.last_name" 
                          placeholder="Last Name" 
                          :readonly="author.found" 
                          required
                        >
                      </div>
                    </div>
                    <div class="form-row">
                      <div class="col-md-6 mb-2">
                        <label class="small text-muted">Country *</label>
                        <CountrySelect 
                          v-model="author.country"
                        />
                      </div>
                      <div class="col-md-6 mb-2">
                        <label class="small text-muted">job_title/Institution *</label>
                        <input 
                          type="text" 
                          class="form-control" 
                          v-model="author.job_title" 
                          placeholder="job_title/Institution" 
                          :readonly="author.found" 
                          required
                        >
                      </div>
                    </div>
                    <div class="form-row">
                      <div class="col-md-12 text-right">
                        <button 
                          type="button" 
                          class="btn btn-danger btn-sm" 
                          @click="removeAuthor(idx)" 
                          v-if="newAbstract.authors.length > 1"
                        >
                          Remove Author
                        </button>
                      </div>
                    </div>
                  </div>
                  <button type="button" class="btn btn-outline-primary btn-sm mt-2" @click="addAuthor">Add Author</button>
                </div>
                
                <div class="form-group">
                  <label for="abstractContent">Content *</label>
                  <quill-editor
                    ref="quillEditor"
                    v-model="newAbstract.content"
                    :options="editorOption"
                    style="height: 300px;"
                  />
                  <!-- <small class="form-text text-muted">
                    {{ newAbstract.content.length }}/800 characters
                  </small> -->
                </div>
              
              </form>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-secondary" @click="closeModal">Cancel</button>
              <button type="button" class="btn btn-primary" @click="handleSubmit" :disabled="submitting">
                {{ submitting ? (isEditing ? 'Updating...' : 'Submitting...') : (isEditing ? 'Update Abstract' : 'Submit Abstract') }}
              </button>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Modal Backdrop -->
      <div class="modal-backdrop fade show" v-if="showSubmitForm || showEditForm"></div>
    </div>
  </Layout>
</template>

<script>
import { quillEditor } from 'vue-quill-editor'
import Quill from 'quill'
import Layout from '@/components/Layout.vue'
import { apiService } from '@/config/api'
import CountrySelect from '@/components/CountrySelect.vue'

export default {
  name: 'MyAbstracts',
  components: {
    Layout,
    CountrySelect,
    quillEditor
  },
  data() {
    return {
      editorOption: {
        theme: 'snow',
        modules: {
          formula: true,
          toolbar: [
            ['bold', 'italic', 'underline', 'strike'],
            ['blockquote', 'code-block'],
            [{ 'header': 1 }, { 'header': 2 }],
            [{ 'list': 'ordered'}, { 'list': 'bullet' }],
            [{ 'script': 'sub'}, { 'script': 'super' }], // 上下标
            [{ 'indent': '-1'}, { 'indent': '+1' }],
            [{ 'direction': 'rtl' }],
            [{ 'size': ['small', false, 'large', 'huge'] }],
            [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
            [{ 'color': [] }, { 'background': [] }],
            [{ 'font': [] }],
            [{ 'align': [] }],
            ['formula'], // 公式按钮
            ['clean']
          ]
        },
        placeholder: 'Insert text here ...'
      },
      selectedFilter: 'all',
      showSubmitForm: false,
      showEditForm: false,
      submitting: false,
      isEditing: false,
      editingAbstractId: null,
      sessions: [], // 会议列表
      allSessionsList: [], // 所有会议列表
      loadingSessions: false, // 加载会议状态
      ticketList: [], // 票价列表（数组）
      loadingTickets: false, // 加载票价状态
      newAbstract: {
        title: '',
        mode: '',
        session_id: '', // 选中的会议ID
        registration_type: '', // 选中的注册类型
        authors: [
          { 
            user_id: null, 
            email: '', 
            first_name: '', 
            last_name: '', 
            title: '', 
            country: '', 
            job_title: '', 
            found: false 
          }
        ],
        content: '', // 确保是字符串类型
      },
      abstracts: []
    }
  },
  computed: {
    filteredAbstracts() {
      if (this.selectedFilter === 'all') {
        return this.abstracts
      }
      
      return this.abstracts.filter(abstract => {
        return abstract.session_id === parseInt(this.selectedFilter)
      })
    },
    // 新增：只显示与mode匹配的票种
    filteredTickets() {
      const mode = (this.newAbstract.mode || '').toLowerCase()
      if (!Array.isArray(this.ticketList) || this.ticketList.length === 0 || !mode) {
        return []
      }
      // 后端返回: 每个ticket包含 { id, type, mode, fee, currency ... }
      // 匹配规则：优先按 type 与 mode 相等，其次按 type 包含/被包含，最后当 mode 为 either 时接受全部
      const candidates = this.ticketList.filter(t => {
        const type = (t.type || '').toLowerCase()
        const tMode = (t.mode || '').toLowerCase()
        if (mode === 'either') return true
        // 有的后端把展示类型放在 type，有的放在 mode，两者都尝试
        return type === mode || tMode === mode || type.includes(mode) || mode.includes(type) || tMode.includes(mode)
      })
      return candidates
    }
  },
  watch: {
    // 监听mode变化，自动设置registration_type
    'newAbstract.mode'() {
      this.updateRegistrationType()
    },
    // 监听ticketList变化
    ticketList() {
      this.updateRegistrationType()
    }
  },
  created() {
    this.fetchAbstracts()
    this.fetchAllSessions()
  },
  mounted() {

    // 抑制Quill.js的弃用警告
    this.originalWarn = console.warn;
    console.warn = function(...args) {
      if (args[0] && typeof args[0] === 'string' && args[0].includes('DOMNodeInserted')) {
        return; // 忽略DOMNodeInserted警告
      }
      this.originalWarn.apply(console, args);
    }.bind(this);

    // 让复制带有 <sub>/<sup> 的内容在粘贴时保留为下标/上标
    this.$nextTick(() => {
      const quill = this.$refs.quillEditor && this.$refs.quillEditor.quill
      if (!quill) return
      const addMatcher = (tagName, script) => {
        quill.clipboard.addMatcher(tagName, (node, delta) => {
          delta.ops = (delta.ops || []).map(op => {
            op.attributes = Object.assign({}, op.attributes, { script })
            return op
          })
          return delta
        })
      }
      addMatcher('SUB', 'sub')
      addMatcher('SUP', 'super')

      // 将纯文本中的常见化学式自动转换为上下标（仅在粘贴时执行）
      quill.clipboard.addMatcher(Node.TEXT_NODE, (node, delta) => {
        const ops = []
        ;(delta.ops || []).forEach(op => {
          if (typeof op.insert !== 'string') { ops.push(op); return }
          const text = op.insert
          let lastIndex = 0
          // 化学式下标：元素符号后的数字或表达式（支持 2−2x / 2-2x / x）
          const regex = /([A-Z][a-z]?)(\d+(?:[−-]\d*x)?|x)/g
          let m
          while ((m = regex.exec(text)) !== null) {
            const [full, symbol, subPart] = m
            const start = m.index
            if (start > lastIndex) {
              ops.push({ insert: text.slice(lastIndex, start) })
            }
            ops.push({ insert: symbol })
            ops.push({ insert: subPart, attributes: { script: 'sub' } })
            lastIndex = start + full.length
          }
          let rest = text.slice(lastIndex)
          if (rest) {
            // 额外：价态/自由基，处理 2-, 3+, 以及 “•−” 等
            const ionRegex = /(\d+)?(•)?([+−-])/g
            let idx = 0, r
            const tempOps = []
            while ((r = ionRegex.exec(rest)) !== null) {
              const s = r.index
              if (s > idx) tempOps.push({ insert: rest.slice(idx, s) })
              if (r[1]) tempOps.push({ insert: r[1], attributes: { script: 'super' } })
              if (r[2]) tempOps.push({ insert: r[2], attributes: { script: 'super' } })
              tempOps.push({ insert: r[3], attributes: { script: 'super' } })
              idx = s + r[0].length
            }
            if (idx < rest.length) tempOps.push({ insert: rest.slice(idx) })
            ops.push(...tempOps)
          }
        })
        delta.ops = ops
        return delta
      })
    })
  },
  beforeDestroy() {
    // 移除公式相关的清理代码
    
    // 恢复console.warn
    if (this.originalWarn) {
      console.warn = this.originalWarn;
    }
  },
  methods: {
    async fetchAllSessions() {
      const res = await apiService.sessions.getList({ page: 1, limit: 100 })
      if (res.data && res.data.code === 1) {
        this.allSessionsList = res.data.data.list || []
      }
    },
    // 获取摘要列表
    async fetchAbstracts() {
      try {
        const res = await apiService.abstracts.getList()
        if (res.data && res.data.code === 1) {
          // 如果API返回的数据有authors_new字段，使用它；否则保持默认数据
          const apiData = res.data.data.list || []
          if (apiData.length > 0 && apiData[0].hasOwnProperty('authors_new')) {
            this.abstracts = apiData
          } else {
            console.log('API返回的数据没有authors_new字段，使用默认数据')
            // 保持默认的模拟数据
          }
        } else {
          console.error('获取摘要列表失败:', res.data)
          // 保持默认的模拟数据
        }
      } catch (error) {
        console.error('获取摘要列表出错:', error)
        // 保持默认的模拟数据
      }
    },
    // 获取会议列表
    async fetchSessions() {
      this.loadingSessions = true
      try {
        const res = await apiService.sessions.getSessionList({ page: 1, limit: 100 })
        if (res.data && res.data.code === 1) {
          this.sessions = res.data.data.list || []
        } else {
          console.error('获取会议列表失败:', res.data)
          this.sessions = []
        }
      } catch (error) {
        console.error('获取会议列表出错:', error)
        // 使用模拟数据作为备用
        this.sessions = [ ]
      } finally {
        this.loadingSessions = false
      }
    },
    
    // 获取票价列表
    async getTicketList(sessionId) {
      if (!sessionId) {
        this.ticketList = []
        return
      }

      this.loadingTickets = true
      try {
        const res = await apiService.sessions.getTicketList({ session_id: sessionId })
        
        if (res.data && res.data.code === 1) {
          // 根据API返回的数据结构，票价列表在 data.abstract 数组中
          this.ticketList = Array.isArray(res.data.data.abstract) ? res.data.data.abstract : []
        } else {
          this.ticketList = []
        }
      } catch (error) {
        this.ticketList = []
      } finally {
        this.loadingTickets = false
      }
    },
    
    // Session选择变化时的处理
    onSessionChange() {
      // 清空之前选择的注册类型
      this.newAbstract.registration_type = ''
      // 获取新的票价列表
      this.getTicketList(this.newAbstract.session_id)
    },
    
    // 显示提交表单时获取会议列表
    async showSubmitModal() {
      this.showSubmitForm = true
      await this.fetchSessions()
    },
    // 获取摘要状态样式类
    getStatusText(status) {
      switch (status) {
        case 0: return 'Pending review'
        case 1: return 'Accepted'
        case 2: return 'Rejected'
        default: return 'Unknown Status'
      }
    },
    
    getStatusBadgeClass(status) {
      switch (status) {
        case 'Submitted': return 'badge-warning'
        case 'Accepted': return 'badge-success'
        case 'Rejected': return 'badge-danger'
        case 'Draft': return 'badge-secondary'
        default: return 'badge-secondary'
      }
    },
    viewAbstract(abstract) {
      // 只保存必要的数据，避免循环引用
      const abstractData = {
        id: abstract.id,
        title: abstract.title,
        content: abstract.content,
        createtime: abstract.createtime,
        status: abstract.status,
        ab_no: abstract.ab_no,
        session: abstract.session,
        authors_new: abstract.authors_new,
        reject: abstract.reject
      }
      sessionStorage.setItem('abstract_detail', JSON.stringify(abstractData))
      this.$router.push(`/abstract-details/${abstract.id}`)
    },
    editAbstract(abstract) {
      this.isEditing = true
      this.showEditForm = true
      this.editingAbstractId = abstract.id
      
      // 先获取会议列表，然后设置数据
      this.fetchSessions().then(() => {
        this.newAbstract = {
          id: abstract.id,
          title: abstract.title,
          mode: abstract.mode,
          session_id: abstract.session_id,
          registration_type: abstract.registration_type,
          authors: abstract.authors_new ? abstract.authors_new.map(author => ({
            user_id: author.user_id,
            email: author.email,
            first_name: author.first_name,
            last_name: author.last_name,
            title: author.title,
            country: author.country,
            job_title: author.job_title,
            found: false
          })) : [],
          content: typeof abstract.content === 'string' ? abstract.content : String(abstract.content || '')
        }
        
        // 使用$nextTick确保DOM更新后再设置Quill编辑器内容
        this.$nextTick(() => {
          if (this.$refs.quillEditor && this.$refs.quillEditor.quill && abstract.content) {
            // 确保content是字符串类型
            const content = typeof abstract.content === 'string' ? abstract.content : String(abstract.content || '')
            this.$refs.quillEditor.quill.root.innerHTML = content
          }
        })
        
        // 加载对应的票价列表
        if (abstract.session_id) {
          this.getTicketList(abstract.session_id)
        }
      })
    },
    async updateAbstract() {
      // 获取Quill编辑器的HTML内容
      if (this.$refs.quillEditor && this.$refs.quillEditor.quill) {
        let html = this.$refs.quillEditor.quill.root.innerHTML
        html = this.normalizeChemistryHTML(html)
        this.newAbstract.content = html
      }

      if (this.newAbstract.content.length > 2000) {
        alert('Abstract content cannot exceed 2000 characters')
        return
      }
      
      // 验证会议选择
      if (!this.newAbstract.session_id) {
        alert('Please select a session')
        return
      }
      
      // 验证注册类型选择
      if (!this.newAbstract.registration_type) {
        alert('Please select a registration type')
        return
      }
      
      // 校验所有作者
      for (const author of this.newAbstract.authors) {
        if (!author.email || !author.first_name || !author.last_name || !author.country || !author.job_title) {
          alert('Please fill in all required author information (Email, First Name, Last Name, Country, job_title)')
          return
        }
      }
      this.submitting = true
      try {
        // 准备提交数据，确保user_id字段正确传递
        const submitData = {
          id: this.newAbstract.id, // 添加ID
          title: this.newAbstract.title,
          mode: this.newAbstract.mode,
          session_id: this.newAbstract.session_id,
          // registration_type: this.newAbstract.registration_type,
          content: this.newAbstract.content,
          authors: this.newAbstract.authors.map(author => ({
            user_id: author.user_id, // 确保user_id字段被传递
            email: author.email,
            first_name: author.first_name,
            last_name: author.last_name,
            title: author.title,
            country: author.country,
            job_title: author.job_title
          }))
        }
        
        const res = await apiService.abstracts.update(submitData)

        if (res.data && res.data.code === 1) {
          alert('Abstract updated successfully!')
          
          // 使用返回的abstracts_id创建订单号
          const abstracts_id = res.data.data.abstract_id || res.data.data.id
          if (abstracts_id) {
            try {
              const billRes = await apiService.abstracts.createAbstractBill({ id: abstracts_id })
              if (billRes.data && billRes.data.code === 1) {
                console.log('订单创建成功:', billRes.data.data)
              } else {
                console.warn('订单创建失败:', billRes.data)
              }
            } catch (billError) {
              console.error('创建订单时出错:', billError)
            }
          }
          
          // 重新获取摘要列表
          await this.fetchAbstracts()
        } else {
          alert(res.data.msg || 'Update failed')
        }
              } catch (error) {
          console.error('Abstract update failed:', error)
          alert('Update failed, please try again')
        } finally {
          this.submitting = false
          // 关闭模态框并重置表单
          this.closeModal()
        }
    },
    async deleteAbstract(abstract) {
      if (confirm(`Are you sure you want to delete the abstract "${abstract.title}"?`)) {
        try {
          const res = await apiService.abstracts.delete({ id: abstract.id })
          if (res && res.data && res.data.code === 1) {
            alert('Delete successful')
            this.fetchAbstracts()
          } else {
            alert((res && res.data && res.data.msg) || 'Delete failed')
          }
        } catch (e) {
          console.error('Delete error:', e)
          alert('Delete failed')
        }
      }
    },
    formatSession(session) {
      if (!session) return 'TBD'
      if (typeof session === 'string') return session
      if (typeof session === 'object') {
        const no = session.session_no || session.no || sessionno || ''
        const title = session.title || session.sessionname || session.name || ''
        const parts = [no, title].filter(Boolean)
        return parts.length > 0 ? parts.join(' - ') : 'TBD'
      }
      return 'TBD'
    },
    async fetchAuthorInfo(idx) {
      const author = this.newAbstract.authors[idx]
      if (!author.email) return
      try {
        const res = await apiService.auth.getUserByEmail(author.email)
        if (res.data && res.data.code === 1 && res.data.data) {
          this.$set(this.newAbstract.authors, idx, {
            ...author,
            user_id: res.data.data.id,
            first_name: res.data.data.first_name || '',
            last_name: res.data.data.last_name || '',
            title: res.data.data.title || '',
            country: res.data.data.country || '',
            job_title: res.data.data.job_title || res.data.data.institution || '',
            found: true
          })
        } else {
          this.$set(this.newAbstract.authors, idx, {
            ...author,
            user_id: null,
            found: false
          })
        }
      } catch (e) {
        this.$set(this.newAbstract.authors, idx, {
          ...author,
          user_id: null,
          first_name: '',
          last_name: '',
          title: '',
          country: '',
          job_title: '',
          found: false
        })
      }
    },
    formatDate(dateValue) {
      if (!dateValue) return 'N/A'
      
      // 如果是时间戳（数字）
      if (typeof dateValue === 'number') {
        // 判断是否为秒级时间戳（小于10000000000），如果是则转换为毫秒
        let timestamp = dateValue
        if (timestamp < 10000000000) {
          timestamp = timestamp * 1000
        }
        
        const date = new Date(timestamp)
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        })
      }
      
      // 如果是字符串格式的日期
      if (typeof dateValue === 'string') {
        const date = new Date(dateValue)
        if (isNaN(date.getTime())) {
          // 如果无法解析，返回原值
          return dateValue
        }
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        })
      }
      
      return 'Invalid Date'
    },
    addAuthor() {
      this.newAbstract.authors.push({ user_id: null, email: '', first_name: '', last_name: '', title: '', country: '', job_title: '', found: false })
    },
    removeAuthor(idx) {
      this.newAbstract.authors.splice(idx, 1)
    },
    async submitAbstract() {
      // 获取Quill编辑器的HTML内容
      if (this.$refs.quillEditor && this.$refs.quillEditor.quill) {
        let html = this.$refs.quillEditor.quill.root.innerHTML
        html = this.normalizeChemistryHTML(html)
        this.newAbstract.content = html
      }
      
      if (this.newAbstract.content.length > 2000) {
        alert('Abstract content cannot exceed 2000 characters')
        return
      }
      
      // 验证会议选择
      if (!this.newAbstract.session_id) {
        alert('Please select a session')
        return
      }
      
      // 验证注册类型选择
      if (!this.newAbstract.registration_type) {
        alert('Please select a registration type')
        return
      }
      
      // 校验所有作者
      for (const author of this.newAbstract.authors) {
        if (!author.email || !author.first_name || !author.last_name || !author.country || !author.job_title) {
          alert('Please fill in all required author information (Email, First Name, Last Name, Country, job_title)')
          return
        }
      }
      this.submitting = true
      try {
        // 准备提交数据，确保user_id字段正确传递
        const submitData = {
          title: this.newAbstract.title,
          mode: this.newAbstract.mode,
          session_id: this.newAbstract.session_id,
          content: this.newAbstract.content,
          authors: this.newAbstract.authors.map(author => ({
            user_id: author.user_id, // 确保user_id字段被传递
            email: author.email,
            first_name: author.first_name,
            last_name: author.last_name,
            title: author.title,
            country: author.country,
            job_title: author.job_title
          }))
        }
        
        const res = await apiService.abstracts.create(submitData)
        
        // 提交成功后的处理
        if (res.data && res.data.code === 1) {
          alert('Abstract submitted successfully!')
          
          // 使用返回的abstracts_id创建订单号
          const abstracts_id = res.data.data.abstract_id || res.data.data.id
          if (abstracts_id) {
            try {
              const billRes = await apiService.abstracts.createAbstractBill({ id: abstracts_id })
              if (billRes.data && billRes.data.code === 1) {
                console.log('订单创建成功:', billRes.data.data)
              } else {
                console.warn('订单创建失败:', billRes.data)
              }
            } catch (billError) {
              console.error('创建订单时出错:', billError)
            }
          }
          
          // 重新获取摘要列表
          await this.fetchAbstracts()
        } else {
          alert(res.data.msg || 'Submission failed')
        }
              } catch (error) {
          console.error('Abstract submission failed:', error)
          alert('Submission failed, please try again')
        } finally {
          this.submitting = false
          // 关闭模态框并重置表单
          this.closeModal()
        }
    },
    filterAbstracts() {
      console.log('Filter changed to:', this.selectedFilter)
    },
    clearFilter() {
      this.selectedFilter = 'all'
    },
    closeModal() {
      this.showSubmitForm = false
      this.showEditForm = false
      this.isEditing = false
      this.editingAbstractId = null // 清空编辑的摘要ID
      // 重置表单
      this.newAbstract = {
        title: '',
        // category: '',
        mode: '',
        session_id: '',
        registration_type: '',
        authors: [
          { 
            user_id: null, 
            email: '', 
            first_name: '', 
            last_name: '', 
            title: '', 
            country: '', 
            job_title: '', 
            found: false 
          }
        ],
        content: '',
        // keywords: ''
      }
      // 清空票价列表
      this.ticketList = []
    },
    getAuthorsList(authorsData) {
      if (!authorsData) return []
      
      // 如果是数组格式（authors_new）
      if (Array.isArray(authorsData)) {
        return authorsData.map(author => {
          const title = author.title ? `${author.title}. ` : ''
          return `${title}${author.first_name || ''} ${author.last_name || ''} <${author.email || ''}> (${author.job_title || ''}, ${author.country || ''})`
        }).filter(author => author.trim())
      }
      
      // 如果是字符串格式（兼容旧格式）
      if (typeof authorsData === 'string') {
        return authorsData.split(',').filter(author => author.trim())
      }
      
      return []
    },
    async handleSubmit() {
      if (this.isEditing) {
        await this.updateAbstract()
      } else {
        await this.submitAbstract()
      }
    },
    updateRegistrationType() {
      const matches = this.filteredTickets
      if (matches.length > 0) {
        this.newAbstract.registration_type = matches[0].id
      } else {
        this.newAbstract.registration_type = ''
      }
    },
    // 将常见化学式文本转换为带上下标的 HTML，以兼容从 Word/PDF 粘贴的纯文本
    normalizeChemistryHTML(html) {
      if (!html || typeof html !== 'string') return html
      // 1) 元素符号 + 下标（支持 2−2x / 2-2x / x）
      html = html.replace(/([A-Z][a-z]?)(\d+(?:[−-]\d*x)?|x)/g, '$1<sub>$2</sub>')
      // 2) 价态/自由基：把 “2- / 3+ / •−” 等作为上标（尽量避免误伤，要求前面是字母或右括号）
      html = html.replace(/([A-Za-z\)])(\d*•?[+−-])/g, '$1<sup>$2</sup>')
      return html
    }
  }
}
</script>

<style scoped>
.abstract-list {
  margin-top: 20px;
}

.abstract-item {
  margin-bottom: 20px;
  transition: box-shadow 0.3s ease;
}

.abstract-item:hover {
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.abstract-meta {
  margin-top: 10px;
}

.abstract-meta .badge {
  margin-right: 5px;
}

.authors-list {
  margin-top: 8px;
}

.author-item {
  padding: 2px 0;
  font-size: 14px;
  color: #666;
  border-bottom: 1px solid #f0f0f0;
}

.author-item:last-child {
  border-bottom: none;
}

.abstract-info p {
  margin-bottom: 5px;
  font-size: 14px;
}

.abstract-actions {
  margin-top: 15px;
}

.card-title {
  color: #2c3e50;
  margin-bottom: 10px;
}

.card-text {
  color: #666;
  line-height: 1.5;
}

.card-body-content {
  display: -webkit-box;
  -webkit-line-clamp: 2;   /* 限制为2行 */
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.modal {
  display: block;
  background-color: rgba(0,0,0,0.5);
  z-index: 1050;
}

.modal-dialog {
  max-width: 800px;
  max-height: 90vh;
}

/* 可滚动表单样式 */
.scrollable-form {
  max-height: 70vh;
  overflow-y: auto;
}

.scrollable-form::-webkit-scrollbar {
  width: 8px;
}

.scrollable-form::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.scrollable-form::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 4px;
}

.scrollable-form::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* 模态框内容区域样式 */
.modal-body {
  max-height: 70vh;
  overflow: hidden;
}

.modal-content {
  max-height: 90vh;
  overflow: hidden;
}

/* 表单组间距 */
.scrollable-form .form-group {
  margin-bottom: 20px;
}

.scrollable-form .form-group:last-child {
  margin-bottom: 0;
  padding-bottom: 20px;
}

.author-block {
  background-color: #f8f9fa;
  border: 1px solid #dee2e6 !important;
}

.author-block:hover {
  background-color: #e9ecef;
  border-color: #adb5bd !important;
}

.author-block .form-row {
  margin-left: -5px;
  margin-right: -5px;
}

.author-block .col-md-6,
.author-block .col-md-12 {
  padding-left: 5px;
  padding-right: 5px;
}

.author-block label {
  font-size: 12px;
  font-weight: 500;
  color: #495057;
  margin-bottom: 4px;
}

.author-block .form-control {
  font-size: 14px;
}

.author-block .form-control:read-only {
  /* background-color: #e9ecef; */
  color: #6c757d;
}

/* 会议选择下拉框样式 */
.session-select {
  margin-bottom: 20px;
}

.session-select .form-control {
  font-size: 14px;
}

.session-select option {
  padding: 8px;
}

/* 加载状态样式 */
.loading-sessions {
  color: #6c757d;
  font-style: italic;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .abstract-actions {
    margin-top: 10px;
  }
  
  .abstract-actions .btn {
    margin-bottom: 5px;
    width: 100%;
  }
  
  .author-block .form-row {
    margin-left: 0;
    margin-right: 0;
  }
  
  .author-block .col-md-6,
  .author-block .col-md-12 {
    padding-left: 0;
    padding-right: 0;
  }
}


</style> 